/**
 * Collection Status — gamification tiers based on number of reviews/ratings.
 *
 * Tiers:
 *   < 5  reviews → no badge
 *   5+   reviews → Bronze 🥉
 *   10+  reviews → Silver 🥈
 *   20+  reviews → Gold 🥇
 *   30+  reviews → Platinum 💎
 *   50+  reviews → Diamond 👑
 */

export type CollectionTier = 'none' | 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';

export interface CollectionStatus {
  tier: CollectionTier;
  label: string;
  icon: string;
  count: number;
  nextTier: { label: string; icon: string; remaining: number } | null;
}

const TIERS: { min: number; tier: CollectionTier; label: string; icon: string }[] = [
  { min: 50, tier: 'diamond', label: 'Diamond', icon: '👑' },
  { min: 30, tier: 'platinum', label: 'Platinum', icon: '💎' },
  { min: 20, tier: 'gold', label: 'Gold', icon: '🥇' },
  { min: 10, tier: 'silver', label: 'Silver', icon: '🥈' },
  { min: 5, tier: 'bronze', label: 'Bronze', icon: '🥉' },
];

export function getCollectionStatus(reviewCount: number): CollectionStatus {
  for (let i = 0; i < TIERS.length; i++) {
    const current = TIERS[i]!;
    if (reviewCount >= current.min) {
      const nextTierDef = i > 0 ? TIERS[i - 1]! : null;
      return {
        tier: current.tier,
        label: current.label,
        icon: current.icon,
        count: reviewCount,
        nextTier: nextTierDef
          ? { label: nextTierDef.label, icon: nextTierDef.icon, remaining: nextTierDef.min - reviewCount }
          : null,
      };
    }
  }

  // Below bronze
  const lowestTier = TIERS[TIERS.length - 1]!;
  return {
    tier: 'none',
    label: '',
    icon: '',
    count: reviewCount,
    nextTier: { label: lowestTier.label, icon: lowestTier.icon, remaining: lowestTier.min - reviewCount },
  };
}

/** All tiers for milestone display (ordered from lowest to highest) */
export const ALL_TIERS = [...TIERS].reverse();
